import { useCallback, useMemo, useRef, useState } from 'react';
import {
    computeDiff,
    getRemainingByKey,
    isDiffEmpty,
    setColumn,
    setMany,
    setRow,
    toggleCell,
} from './assignmentModel';

const EMPTY = {};

const useAssignmentDraft = (licences) => {
    const [state, setStateValue] = useState({ baseline: EMPTY, draft: EMPTY });
    const stateRef = useRef(state);
    const { baseline, draft } = state;

    const setState = useCallback((next) => {
        stateRef.current = next;
        setStateValue(next);
    }, []);

    const reset = useCallback((nextBaseline) => {
        setState({ baseline: nextBaseline || EMPTY, draft: nextBaseline || EMPTY });
    }, [setState]);

    const rebase = useCallback((nextBaseline, intendedDraft) => {
        const base = nextBaseline || EMPTY;
        const nextDraft = Object.keys(base).reduce((items, key) => {
            items[key] = intendedDraft && intendedDraft[key] ? intendedDraft[key] : base[key];
            return items;
        }, {});
        setState({ baseline: base, draft: nextDraft });
    }, [setState]);

    const discard = useCallback(() => {
        setState({ ...stateRef.current, draft: stateRef.current.baseline });
    }, [setState]);

    const run = useCallback((operation) => {
        const current = stateRef.current;
        const result = operation(current.draft, current.baseline);
        if (result.draft !== current.draft) setState({ ...current, draft: result.draft });
        return result;
    }, [setState]);

    const toggle = useCallback((key, userId) => run(
        (current, base) => toggleCell(current, base, licences, key, userId),
    ), [licences, run]);

    const setColumnUsers = useCallback((key, userIds, assign) => run(
        (current, base) => setColumn(current, base, licences, key, userIds, assign),
    ), [licences, run]);

    const setRowLicences = useCallback((userId, keys, assign) => run(
        (current, base) => setRow(current, base, licences, userId, keys, assign),
    ), [licences, run]);

    const setManyAssignments = useCallback((keys, userIds, assign) => run(
        (current, base) => setMany(current, base, licences, keys, userIds, assign),
    ), [licences, run]);

    const diff = useMemo(() => computeDiff(baseline, draft), [baseline, draft]);
    const remainingByKey = useMemo(
        () => getRemainingByKey(licences, baseline, draft),
        [licences, baseline, draft],
    );

    return {
        baseline,
        draft,
        diff,
        remainingByKey,
        isDirty: !isDiffEmpty(diff),
        reset,
        rebase,
        discard,
        toggle,
        setColumnUsers,
        setRowLicences,
        setManyAssignments,
    };
};

export default useAssignmentDraft;
